"use client";

import { useEffect } from "react";
import { useParams, usePathname } from "next/navigation";
import posthog from "posthog-js";
import { readAnalyticsConsent } from "@/components/analytics/browser-attribution";
import { subscribeToBrowserConsentCategory } from "@/components/analytics/browser-consent-lifecycle";
import { POSTHOG_BROWSER_INITIALIZED_EVENT } from "@/lib/analytics/posthog-browser-lifecycle";
import {
  ANALYTICS_LOCATION_CHANGE_EVENT,
  isAnalyticsLoadSuppressedUrl,
} from "@/lib/analytics/suppressed-paths";
import {
  annotateRouteTemplate,
  metricTime,
  recordRouteMark,
  routeAt,
  routeTemplateFor,
  webVitalsEventProperties,
  type AttributedWebVitalsMetric,
  type RouteMark,
} from "@/lib/analytics/web-vitals-attribution";

// web-vitals observers live for the whole page and cannot be unregistered, so
// registration and the route timeline are page-wide: a cross-route-group
// navigation remounts this component (each layout mounts its own) but must
// neither register a second set of callbacks nor leave the first set
// attributing metrics to a timeline nobody updates any more.
let registrationStarted = false;
let timelineInstalled = false;
const routeMarks: RouteMark[] = [];

function currentUrlSuppressed(): boolean {
  return isAnalyticsLoadSuppressedUrl(
    window.location.pathname,
    window.location.search,
    window.location.hash,
  );
}

function recordCurrentLocation(at: number): void {
  recordRouteMark(routeMarks, {
    at,
    path: window.location.pathname,
    template: null,
    suppressed: currentUrlSuppressed(),
  });
}

/**
 * The timeline is driven by the head-script navigation barrier's location
 * event (every history write and popstate), page-wide, not by this
 * component's own render. It is installed when this module first runs; a
 * navigation BEFORE that cannot be placed in time, so the span up to now is
 * recorded as unknown (never sent) rather than guessed.
 */
function installRouteTimeline(): void {
  if (timelineInstalled || typeof window === "undefined") return;
  timelineInstalled = true;
  const navigation = performance.getEntriesByType?.("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined;
  let initialPath: string | null = null;
  try {
    initialPath = navigation ? new URL(navigation.name).pathname : null;
  } catch {
    initialPath = null;
  }
  if (initialPath === null || initialPath === window.location.pathname) {
    recordCurrentLocation(0);
  } else {
    recordRouteMark(routeMarks, {
      at: 0,
      path: initialPath,
      template: null,
      suppressed: true,
    });
    recordCurrentLocation(performance.now());
  }
  window.addEventListener(ANALYTICS_LOCATION_CHANGE_EVENT, () =>
    recordCurrentLocation(performance.now()),
  );
}

function sendMetric(metric: AttributedWebVitalsMetric): void {
  if (!posthog.__loaded || readAnalyticsConsent() !== "accepted") return;
  const properties = webVitalsEventProperties(
    metric,
    routeAt(routeMarks, metricTime(metric)),
  );
  if (!properties) return;
  posthog.capture("$web_vitals", properties);
}

function registerWhenReady(): void {
  if (
    registrationStarted ||
    !posthog.__loaded ||
    readAnalyticsConsent() !== "accepted"
  ) {
    return;
  }
  registrationStarted = true;
  void import("web-vitals/attribution").then(
    ({ onLCP, onINP, onCLS }) => {
      if (!posthog.__loaded || readAnalyticsConsent() !== "accepted") {
        // Consent was withdrawn while the chunk loaded: register on the next
        // accepted (re-)initialisation instead.
        registrationStarted = false;
        return;
      }
      onLCP(sendMetric);
      onINP(sendMetric);
      onCLS(sendMetric);
    },
    () => {
      registrationStarted = false;
    },
  );
}

export function WebVitalsCapture() {
  const pathname = usePathname();
  const params = useParams();

  // Attach the route pattern (from the router's params) to the timeline mark
  // for this pathname. A mark that never gets one stays unknown and is never
  // sent, so a mis-templated or token-bearing path cannot leave the browser.
  useEffect(() => {
    installRouteTimeline();
    if (pathname === null) return;
    if (window.location.pathname !== pathname) return;
    // React can commit the new route before the barrier's (microtask) location
    // event: open the mark here; the event's duplicate is then a no-op.
    if (routeMarks[routeMarks.length - 1]?.path !== pathname) {
      recordCurrentLocation(performance.now());
    }
    annotateRouteTemplate(routeMarks, pathname, routeTemplateFor(pathname, params));
  }, [pathname, params]);

  useEffect(() => {
    registerWhenReady();
    window.addEventListener(
      POSTHOG_BROWSER_INITIALIZED_EVENT,
      registerWhenReady,
    );
    const unsubscribeConsent = subscribeToBrowserConsentCategory(
      "analytics",
      registerWhenReady,
    );
    return () => {
      window.removeEventListener(
        POSTHOG_BROWSER_INITIALIZED_EVENT,
        registerWhenReady,
      );
      unsubscribeConsent();
    };
  }, []);

  return null;
}
